import React, { Suspense, useMemo } from "react";
import { ChakraProvider } from "@chakra-ui/react";
import {
  StyledEngineProvider,
  ThemeProvider as MuiThemeProvider,
} from "@mui/material";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ApolloProvider } from "@apollo/client/react";
import apolloClient from "./graphql/apolloClient";
import Layout from "./components/Layout";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { getRoleTheme } from "./themes/theme";
import { LoaderProvider } from "./context/LoaderContext";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import PageWithTitle from "./components/PageWithTitle";
import muiTheme from "./themes/muiTheme";
import { ClientProvider } from "./context/ClientContext";
import { DocumentExtractionProvider } from "./context/DocumentExtractionContext";
import { PublicRoute } from "./components/PublicRoute";
import { Toaster } from "react-hot-toast";
import { shouldOpenPartnerCompanyPicker } from "./utils/partnerRoute";
import DeveloperConsoleGate from "./components/DeveloperConsoleGate";
import TenantGate from "./components/TenantGate";
import { PortalOnlyRoute } from "./components/PortalOnlyRoute";
import RouteTransitionTracker from "./components/RouteTransitionTracker";

// ─── Lazy-loaded Pages ──────────────────────────────────────────────────────
const Login = React.lazy(() => import("./pages/Login"));
const Signup = React.lazy(() => import("./pages/Signup"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const AcceptInvitation = React.lazy(() => import("./pages/accept-invitation/AcceptInvitation"));
const Dashboard = React.lazy(() => import("./pages/dashboard/Dashboard"));
const Insights = React.lazy(() => import("./pages/insights/Insights"));
const Manufacturer = React.lazy(() => import("./pages/masterData/manufacturer/Manufacturer"));
const Vendor = React.lazy(() => import("./pages/masterData/vendor/Vendor"));
const WorkOrderStage = React.lazy(() => import("./pages/masterData/workorderStage/WorkOrderStage"));
const MasterDataRequest = React.lazy(() => import("./pages/masterData/masterDataRequest/MasterDataRequest"));
const WorkOrders = React.lazy(() => import("./pages/workorders/WorkOrders"));
const WorkOrderForm = React.lazy(() => import("./pages/workorders/WorkOrderForm"));
const WorkOrderView = React.lazy(() => import("./pages/workorders/WorkOrderView"));
const Support = React.lazy(() => import("./pages/support/Support"));
const Assets = React.lazy(() => import("./pages/assets/Assets"));
const AssetView = React.lazy(() => import("./pages/assets/AssetView"));
const AssetEdit = React.lazy(() => import("./pages/assets/AssetEdit"));
const Team = React.lazy(() => import("./pages/team/Team"));
const Settings = React.lazy(() => import("./pages/settings/Settings"));
const AiMarketplace = React.lazy(() => import("./pages/ai-marketplace/AiMarketplace"));
const Plans = React.lazy(() => import("./pages/plans/Plans"));
const Billing = React.lazy(() => import("./pages/billing/Billing"));
const SubscriptionOverview = React.lazy(() => import("./pages/subscription/SubscriptionOverview"));
const Onboarding = React.lazy(() => import("./pages/onboarding/Onboarding"));
const OnboardingList = React.lazy(() => import("./pages/onboarding/OnboardingList"));
const InspectionOrdersList = React.lazy(() => import("./pages/InspectionOrders/InspectionOrdersList"));
const CreateInspectionOrder = React.lazy(() => import("./pages/InspectionOrders/CreateInspectionOrder"));
const ApproverInspectionView = React.lazy(() => import("./pages/InspectionOrders/ApproverInspectionView"));
const InspectorInspectionView = React.lazy(() => import("./pages/InspectionOrders/InspectorInspectionView"));
const CompanyKnowledge = React.lazy(() => import("./pages/companyKnowledge/CompanyKnowledge"));
const CompanyNotes = React.lazy(() => import("./pages/companyNotes/CompanyNotes"));
const ComplianceSettingsPage = React.lazy(() => import("./pages/complianceSettings/ComplianceSettingsPage"));
const DeveloperConsoleDashboard = React.lazy(() => import("./pages/developer-console/DeveloperConsoleDashboard"));
const DeveloperConsoleApplicationList = React.lazy(() => import("./pages/developer-console/DeveloperConsoleApplicationList"));
const DeveloperConsoleCreateApplication = React.lazy(() => import("./pages/developer-console/DeveloperConsoleCreateApplication"));
const DeveloperConsoleApplicationDetails = React.lazy(() => import("./pages/developer-console/DeveloperConsoleApplicationDetails"));
const DeveloperConsoleEditApplication = React.lazy(() => import("./pages/developer-console/DeveloperConsoleEditApplication"));
const DeveloperConsoleDocs = React.lazy(() => import("./pages/developer-console/DeveloperConsoleDocs"));
const OAuthConsent = React.lazy(() => import("./pages/oauth-consent/OAuthConsent"));

/** Maps legacy /app-marketplace/* URLs to /developer-console/*. */
const LegacyAppMarketplaceRedirect: React.FC = () => {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/app-marketplace\/?/, "");
  const path = "/developer-console" + (rest ? `/${rest}` : "");
  return <Navigate to={`${path}${location.search}${location.hash}`} replace />;
};
const AssetOnboarding = React.lazy(() => import("./pages/assetOnboarding/AssetOnboarding"));
const Obligations = React.lazy(() => import("./pages/obligations/Obligations"));
const TaskList = React.lazy(() => import("./pages/obligations/TaskList"));
const ObligationsBrowser = React.lazy(() => import("./pages/obligationsBrowser/ObligationsBrowser"));
const CompanySelection = React.lazy(() => import("./pages/partner/CompanySelection"));
const DocumentLibrary = React.lazy(() => import("./pages/documentLibrary/DocumentLibrary"));
const LocationMap = React.lazy(() => import("./pages/locationMap/LocationMap"));
const PublicQrPage = React.lazy(() => import("./pages/publicQr/PublicQrPage"));

const muiCache = createCache({ key: "mui", prepend: true });
const chakraCache = createCache({ key: "chakra" });

function App() {
  const chakraTheme = getRoleTheme("user"); // or 'user', 'manager', etc. based on your role logic
  const memoizedMuiTheme = useMemo(() => muiTheme, []);

  return (
    <TenantGate>
    <ApolloProvider client={apolloClient}>
      <Toaster position="top-right" />
      <CacheProvider value={muiCache}>
        <StyledEngineProvider injectFirst>
          <ChakraProvider theme={chakraTheme}>
            <CacheProvider value={chakraCache}>
              <MuiThemeProvider theme={memoizedMuiTheme}>
                <BrowserRouter>
                  <RouteTransitionTracker />
                  <AuthProvider>
                    <DocumentExtractionProvider>
                    <ClientProvider>
                      <LoaderProvider>
                        <Suspense fallback={null}>
                        <Routes>
                          <Route
                            path="/login"
                            element={
                              <PublicRoute>
                                <Login />
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/:schemaName/signup"
                            element={
                              <PublicRoute>
                                <PortalOnlyRoute>
                                  <Signup />
                                </PortalOnlyRoute>
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/signup"
                            element={
                              <PublicRoute>
                                <PortalOnlyRoute>
                                  <Signup />
                                </PortalOnlyRoute>
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/forgot-password"
                            element={
                              <PublicRoute>
                                <ForgotPassword />
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/:schemaName/forgot-password"
                            element={
                              <PublicRoute>
                                <ForgotPassword />
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/reset-password"
                            element={
                              <PublicRoute>
                                <ResetPassword />
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/accept-invitation"
                            element={<AcceptInvitation />}
                          />
                          <Route
                            path="/developer-console/docs"
                            element={
                              <PageWithTitle title="Developer Console · Documentation">
                                <DeveloperConsoleDocs />
                              </PageWithTitle>
                            }
                          />
                          <Route path="/oauth/consent" element={<OAuthConsent />} />
                          <Route
                            path="/qr/:token"
                            element={
                              <PageWithTitle title="Shared via QR">
                                <PublicQrPage />
                              </PageWithTitle>
                            }
                          />
                          <Route
                            element={
                              <ProtectedRoute>
                                <Layout />
                              </ProtectedRoute>
                            }
                          >
                            <Route
                              path="/dashboard"
                              element={
                                <PageWithTitle title="Dashboard">
                                  <Dashboard />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/select-company"
                              element={
                                <PageWithTitle title="Select company">
                                  <CompanySelection />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/insights"
                              element={
                                <PageWithTitle title="Insights">
                                  <Insights />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/manufacturer"
                              element={
                                <PageWithTitle title="Manufacturer">
                                  <Manufacturer />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/vendor"
                              element={
                                <PageWithTitle title="Vendor">
                                  <Vendor />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/masterdatarequest"
                              element={
                                <PageWithTitle title="Master Data Request">
                                  <MasterDataRequest />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/workorderstage"
                              element={
                                <PageWithTitle title="Work Order Stages">
                                  <WorkOrderStage />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/workorders"
                              element={
                                <PageWithTitle title="Work Orders">
                                  <WorkOrders />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/workorders/createworkorder"
                              element={
                                <PageWithTitle title="Create Work Order">
                                  <WorkOrderForm />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/workorders/edit/:id"
                              element={
                                <PageWithTitle title="Edit Work Order">
                                  <WorkOrderForm />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/workorders/view/:id"
                              element={
                                <PageWithTitle title="Work Orders > View Order">
                                  <WorkOrderView />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/support"
                              element={
                                <PageWithTitle title="Support">
                                  <Support />
                                </PageWithTitle>
                              }

                            />
                            <Route
                              path="/assets"
                              element={
                                <PageWithTitle title="Assets">
                                  <Assets />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/assets/view/:id"
                              element={
                                <PageWithTitle title="Asset Details">
                                  <AssetView />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/assets/edit/:id"
                              element={
                                <PageWithTitle title="Edit Asset">
                                  <AssetEdit />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/team"
                              element={
                                <PageWithTitle title="Team">
                                  <Team />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/locationmap"
                              element={
                                <PageWithTitle title="Location Map">
                                  <LocationMap />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/settings"
                              element={
                                <PageWithTitle title="Settings">
                                  <Settings />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/ai-marketplace"
                              element={
                                <PageWithTitle title="AI Marketplace">
                                  <AiMarketplace />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/plans"
                              element={
                                <PageWithTitle title="Plans">
                                  <Plans />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/billing"
                              element={
                                <PageWithTitle title="Billing">
                                  <Billing />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/inspection-orders"
                              element={
                                <PageWithTitle title="Inspection Orders">
                                  <InspectionOrdersList />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/inspection-orders/create"
                              element={
                                <PageWithTitle title="Create Inspection Order">
                                  <CreateInspectionOrder />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/inspection-orders/approver/:id"
                              element={
                                <PageWithTitle title="Approver Inspection View">
                                  <ApproverInspectionView />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/inspection-orders/inspector/:id"
                              element={
                                <PageWithTitle title="Inspector Inspection View">
                                  <InspectorInspectionView />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/subscription"
                              element={
                                <PageWithTitle title="Plan & Billing">
                                  <SubscriptionOverview />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/onboarding"
                              element={
                                <PageWithTitle title="Onboarding">
                                  <Onboarding />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/onboardingsessions"
                              element={
                                <PageWithTitle title="Onboarding Sessions">
                                  <OnboardingList />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/company-knowledge"
                              element={
                                <PageWithTitle title="Company Knowledge">
                                  <CompanyKnowledge />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/company-notes"
                              element={
                                <PageWithTitle title="Company Notes">
                                  <CompanyNotes />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/compliance-settings"
                              element={
                                <PageWithTitle title="Compliance Settings">
                                  <ComplianceSettingsPage />
                                </PageWithTitle>
                              }
                            />
                            <Route path="/app-marketplace/*" element={<LegacyAppMarketplaceRedirect />} />
                            <Route
                              path="/developer-console"
                              element={
                                <DeveloperConsoleGate>
                                  <PageWithTitle title="Developer Console">
                                    <DeveloperConsoleDashboard />
                                  </PageWithTitle>
                                </DeveloperConsoleGate>
                              }
                            />
                            <Route
                              path="/developer-console/applications"
                              element={
                                <DeveloperConsoleGate>
                                  <PageWithTitle title="Developer Console · Applications">
                                    <DeveloperConsoleApplicationList />
                                  </PageWithTitle>
                                </DeveloperConsoleGate>
                              }
                            />
                            <Route
                              path="/developer-console/applications/new"
                              element={
                                <DeveloperConsoleGate>
                                  <PageWithTitle title="Developer Console · New Application">
                                    <DeveloperConsoleCreateApplication />
                                  </PageWithTitle>
                                </DeveloperConsoleGate>
                              }
                            />
                            <Route
                              path="/developer-console/applications/:id"
                              element={
                                <DeveloperConsoleGate>
                                  <PageWithTitle title="Developer Console · Application Details">
                                    <DeveloperConsoleApplicationDetails />
                                  </PageWithTitle>
                                </DeveloperConsoleGate>
                              }
                            />
                            <Route
                              path="/developer-console/applications/:id/edit"
                              element={
                                <DeveloperConsoleGate>
                                  <PageWithTitle title="Developer Console · Edit Application">
                                    <DeveloperConsoleEditApplication />
                                  </PageWithTitle>
                                </DeveloperConsoleGate>
                              }
                            />
                            {/* <Route
                              path="/obligations"
                              element={
                                <PageWithTitle title="Obligations">
                                  <Obligations />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/obligations/:obligationId"
                              element={
                                <PageWithTitle title="Obligations">
                                  <Obligations />
                                </PageWithTitle>
                              }
                            /> */}
                            {/* Not in the sidebar — direct-URL verification page for browsing/filtering
                                the raw obligation catalog by status/source/location/product type. */}
                            <Route
                              path="/obligations-browser"
                              element={
                                <PageWithTitle title="Obligations Browser">
                                  <ObligationsBrowser />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/task-list"
                              element={
                                <PageWithTitle title="Task List">
                                  <TaskList />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/document-library"
                              element={
                                <PageWithTitle title="Document Library">
                                  <DocumentLibrary />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/asset-onboarding"
                              element={
                                <PageWithTitle title="Asset Onboarding">
                                  <AssetOnboarding />
                                </PageWithTitle>
                              }
                            />
                          </Route>
                          {/* <Route path="*" element={<Login />} /> */}
                          <Route
                            path="/"
                            element={
                              <Navigate
                                to={(() => {
                                  if (!localStorage.getItem("accessToken")) return "/login";
                                  try {
                                    const raw = localStorage.getItem("userData");
                                    if (raw) {
                                      const u = JSON.parse(raw);
                                      if (shouldOpenPartnerCompanyPicker(u)) return "/select-company";
                                    }
                                  } catch {
                                    /* ignore */
                                  }
                                  return "/dashboard";
                                })()}
                                replace
                              />
                            }
                          />
                        </Routes>
                        </Suspense>
                      </LoaderProvider>
                    </ClientProvider>
                    </DocumentExtractionProvider>
                  </AuthProvider>
                </BrowserRouter>
              </MuiThemeProvider>
            </CacheProvider>
          </ChakraProvider>
        </StyledEngineProvider>
      </CacheProvider>
    </ApolloProvider>
    </TenantGate>
  );
}

export default App;
